import { LitElement, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { SELECTORS } from '../../constants/selectors';

const HIDDEN_CLASS = 'tw:hidden';
const FLEX_CLASS = 'tw:flex';

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * 参考小売価格 説明モーダルコンポーネント
 *
 * @element kd-price-reference-modal
 * @description 「参考小売価格」ラベル横の「？」ボタン（[data-price-reference-trigger]）から
 *   開く説明モーダルの開閉・フォーカストラップを制御する。
 *   HTML は Liquid（v2-price-reference-modal）が生成し、本コンポーネントは開閉操作のみを担う。
 *
 *   トリガーは本要素の外側（PDP の価格ブロック / カート明細・サマリー / 商品カード等、
 *   document 内のどこか）に複数存在するため、document レベルでクリック・キーボードを監視する。
 *   商品カードでは「？」が <a> リンク内の span（role="button"）のため、クリック時に
 *   preventDefault してリンク遷移を抑止し、Enter/Space のキーボード起動も処理する。
 *   閉じる経路は: ESC キー / オーバーレイクリック / [data-price-reference-modal-close] クリック。
 *
 *   shell（v2-price-reference-modal）は layout に全ページ共通で 1 つ描画される。
 *   万一複数インスタンスが存在しても document 内で最初の 1 つだけが応答する。
 */
@customElement('kd-price-reference-modal')
export class KdPriceReferenceModal extends LitElement {
  createRenderRoot() {
    return this;
  }

  private previouslyFocusedElement: HTMLElement | null = null;

  render() {
    return nothing;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.querySelector(SELECTORS.PRICE_REFERENCE_MODAL.OVERLAY)?.addEventListener(
      'click',
      this.handleOverlayClick
    );
    this.addEventListener('click', this.handleCloseButtonClick);
    document.addEventListener('keydown', this.handleKeyDown);
    document.addEventListener('click', this.handleExternalTriggerClick);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.querySelector(SELECTORS.PRICE_REFERENCE_MODAL.OVERLAY)?.removeEventListener(
      'click',
      this.handleOverlayClick
    );
    this.removeEventListener('click', this.handleCloseButtonClick);
    document.removeEventListener('keydown', this.handleKeyDown);
    document.removeEventListener('click', this.handleExternalTriggerClick);
  }

  private handleOpen = (): void => {
    const overlay = this.querySelector<HTMLElement>(SELECTORS.PRICE_REFERENCE_MODAL.OVERLAY);
    if (!overlay) return;

    this.previouslyFocusedElement = document.activeElement as HTMLElement;
    overlay.classList.remove(HIDDEN_CLASS);
    overlay.classList.add(FLEX_CLASS);
    overlay.removeAttribute('aria-hidden');

    const firstFocusable = overlay.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    firstFocusable?.focus();
  };

  private handleClose = (): void => {
    const overlay = this.querySelector<HTMLElement>(SELECTORS.PRICE_REFERENCE_MODAL.OVERLAY);
    if (!overlay) return;

    overlay.classList.add(HIDDEN_CLASS);
    overlay.classList.remove(FLEX_CLASS);
    overlay.setAttribute('aria-hidden', 'true');

    this.previouslyFocusedElement?.focus();
    this.previouslyFocusedElement = null;
  };

  private handleCloseButtonClick = (e: Event): void => {
    const target = e.target as HTMLElement;
    if (target.closest(SELECTORS.PRICE_REFERENCE_MODAL.CLOSE)) {
      this.handleClose();
    }
  };

  private handleOverlayClick = (e: Event): void => {
    if (e.target === this.querySelector(SELECTORS.PRICE_REFERENCE_MODAL.OVERLAY)) {
      this.handleClose();
    }
  };

  private handleKeyDown = (e: KeyboardEvent): void => {
    // span トリガー（role="button"、商品カードの <a> リンク内）からのキーボード起動。
    // <button> 版（PDP/カート）は Enter/Space で native click が発火するため click 経路で処理される。
    if (e.key === 'Enter' || e.key === ' ') {
      const target = e.target as HTMLElement | null;
      const trigger = target?.closest?.(SELECTORS.PRICE_REFERENCE_MODAL.EXTERNAL_TRIGGER);
      if (trigger && trigger.tagName !== 'BUTTON' && this.isPrimaryInstance()) {
        e.preventDefault();
        this.handleOpen();
        return;
      }
    }

    const overlay = this.querySelector<HTMLElement>(SELECTORS.PRICE_REFERENCE_MODAL.OVERLAY);
    if (!overlay || overlay.classList.contains(HIDDEN_CLASS)) return;

    if (e.key === 'Escape') {
      this.handleClose();
    } else if (e.key === 'Tab') {
      this.trapFocus(e);
    }
  };

  private trapFocus = (e: KeyboardEvent): void => {
    const overlay = this.querySelector<HTMLElement>(SELECTORS.PRICE_REFERENCE_MODAL.OVERLAY);
    if (!overlay) return;

    const focusable = Array.from(
      overlay.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
    ).filter((el) => !el.closest('[hidden]'));
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  /**
   * kd-price-reference-modal 要素の外側にある「？」トリガーボタン
   * （[data-price-reference-trigger]）からモーダルを開くためのハンドラ。
   * document 全体を監視する。
   *
   * 1 ページ内に複数 kd-price-reference-modal インスタンスが存在する場合（PDP + 何らかの理由で重複等）、
   * すべてが open してしまうのを防ぐため、document 内で最初の 1 つだけが応答する。
   */
  private handleExternalTriggerClick = (e: Event): void => {
    if (!this.isPrimaryInstance()) return;

    const target = e.target as HTMLElement;
    if (!target) return;

    const trigger = target.closest(SELECTORS.PRICE_REFERENCE_MODAL.EXTERNAL_TRIGGER);
    if (!trigger) return;

    // 商品カードでは「？」が <a> リンク内にあるため、リンク遷移を抑止する。
    // PDP/カートの <button type="button"> は既定動作が無いため無害。
    e.preventDefault();
    this.handleOpen();
  };

  /**
   * 自身が document 内で最初の kd-price-reference-modal インスタンスか判定する。
   * 複数インスタンス存在時の二重発火を防ぐためのガード。
   */
  private isPrimaryInstance(): boolean {
    return document.querySelector('kd-price-reference-modal') === this;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kd-price-reference-modal': KdPriceReferenceModal;
  }
}
